import { supabase } from '../lib/supabase';
import openaiService from './openaiService';

/**
 * Document Service
 * CRUD operations for public.documents table
 */

export const documentService = {
  /**
   * Get all documents for the current user
   * @returns {Promise<{data: Array, error: any}>}
   */
  async getUserDocuments() {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching documents:', error);
      return { data: null, error };
    }
  },

  /**
   * Get a single document by ID
   * @param {string} id - Document UUID
   * @returns {Promise<{data: Object, error: any}>}
   */
  async getDocument(id) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching document:', error);
      return { data: null, error };
    }
  },

  /**
   * Create a new document
   * @param {Object} documentData - Document data
   * @param {string} documentData.title - Document title
   * @param {string} documentData.type - Document type ('doc' or 'plan')
   * @param {Object} documentData.content_json - TipTap JSON content
   * @returns {Promise<{data: Object, error: any}>}
   */
  async createDocument(documentData = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const newDocument = {
        user_id: user.id,
        title: documentData.title || 'Untitled',
        type: documentData.type || 'doc',
        content_json: documentData.content_json || {
          type: 'doc',
          content: [{ type: 'paragraph' }]
        }
      };

      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .insert(newDocument)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating document:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      return { data: null, error };
    }
  },

  /**
   * Update a document
   * @param {string} id - Document UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<{data: Object, error: any}>}
   */
  async updateDocument(id, updates) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating document:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      console.error('Updates attempted:', updates);
      return { data: null, error };
    }
  },

  /**
   * Delete a document
   * @param {string} id - Document UUID
   * @returns {Promise<{data: any, error: any}>}
   */
  async deleteDocument(id) {
    try {
      const { error } = await supabase
        .schema('public')
        .from('documents')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting document:', error);
      return { data: null, error };
    }
  },

  /**
   * Create a plan from a focus
   * @param {string} userId - User UUID
   * @param {Object} focus - Focus object with id and title
   * @returns {Promise<{data: Object, error: any}>}
   */
  /**
   * Create a doc from a focus
   * @param {string} userId - User UUID
   * @param {Object} focus - Focus object with id and title
   * @returns {Promise<{data: Object, error: any}>}
   */
  async createDocFromFocus(userId, focus) {
    try {
      const docTitle = focus.title || 'Untitled Focus';
      const starterOutline = {
        type: 'doc',
        content: [
          {
            type: 'heading',
            attrs: { level: 1 },
            content: [{ type: 'text', text: docTitle }]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Key Points' }]
          },
          {
            type: 'bulletList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Define your main points...' }] }]
              }
            ]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Constraints' }]
          },
          {
            type: 'bulletList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'List any constraints...' }] }]
              }
            ]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Next Steps' }]
          },
          {
            type: 'orderedList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'First step...' }] }]
              },
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Second step...' }] }]
              },
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Third step...' }] }]
              }
            ]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Open Questions' }]
          },
          {
            type: 'bulletList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'What needs clarification?' }] }]
              },
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'What decisions are pending?' }] }]
              }
            ]
          }
        ]
      };

      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .insert({
          user_id: userId,
          type: 'doc',
          title: docTitle,
          content_json: starterOutline,
          source_focus_id: focus.id,
          focus_ids: [focus.id]
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating doc from focus:', error);
      return { data: null, error };
    }
  },

  /**
   * BACKWARDS COMPATIBLE ALIAS: createPlanFromFocus
   * @deprecated Use createDocFromFocus instead
   */
  async createPlanFromFocus(userId, focus) {
    return this.createDocFromFocus(userId, focus);
  },

  /**
   * Create a doc from a conversation
   * @param {string} userId - User UUID
   * @param {Object} conversation - Conversation object with id, title, focus_id
   * @param {string} conversationTitle - Title for the doc
   * @returns {Promise<{data: Object, error: any}>}
   */
  async createDocFromConversation(userId, conversation, conversationTitle) {
    try {
      const docTitle = conversationTitle || 'Chat';
      const starterOutline = {
        type: 'doc',
        content: [
          {
            type: 'heading',
            attrs: { level: 1 },
            content: [{ type: 'text', text: docTitle }]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Key Points' }]
          },
          {
            type: 'bulletList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Define your main points...' }] }]
              }
            ]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Constraints' }]
          },
          {
            type: 'bulletList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'List any constraints...' }] }]
              }
            ]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Next Steps' }]
          },
          {
            type: 'orderedList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'First step...' }] }]
              },
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Second step...' }] }]
              },
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Third step...' }] }]
              }
            ]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Open Questions' }]
          },
          {
            type: 'bulletList',
            content: [
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'What needs clarification?' }] }]
              },
              {
                type: 'listItem',
                content: [{ type: 'paragraph', content: [{ type: 'text', text: 'What decisions are pending?' }] }]
              }
            ]
          },
          {
            type: 'heading',
            attrs: { level: 2 },
            content: [{ type: 'text', text: 'Source' }]
          },
          {
            type: 'paragraph',
            content: [
              { type: 'text', text: 'Conversation ID: ' },
              { type: 'text', text: conversation.id, marks: [{ type: 'code' }] }
            ]
          }
        ]
      };

      const insertData = {
        user_id: userId,
        type: 'doc',
        title: docTitle,
        content_json: starterOutline,
        source_conversation_id: conversation.id
      };

      // If conversation has focus_id, attach it
      if (conversation.focus_id) {
        insertData.source_focus_id = conversation.focus_id;
        insertData.focus_ids = [conversation.focus_id];
      }

      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating doc from conversation:', error);
      return { data: null, error };
    }
  },

  /**
   * BACKWARDS COMPATIBLE ALIAS: createPlanFromConversation
   * @deprecated Use createDocFromConversation instead
   */
  async createPlanFromConversation(userId, conversation, conversationTitle) {
    return this.createDocFromConversation(userId, conversation, conversationTitle);
  },

  /**
   * Generate doc content using AI
   * @param {Object} conversation - Conversation object with id, title, focus_id
   * @param {Array} messages - Array of conversation messages
   * @returns {Promise<{data: Object, error: any}>}
   */
  async generateDocContent(conversation, messages) {
    try {
      // Take last 30 messages or 6000 chars, whichever is smaller
      const relevantMessages = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .slice(-30);
      
      let transcript = '';
      for (const msg of relevantMessages) {
        transcript += `${msg.role}: ${msg.content}\n\n`;
        if (transcript.length > 6000) {
          transcript = transcript.slice(0, 6000);
          break;
        }
      }

      const prompt = `You are a doc assistant. Based on this conversation, generate a structured document.

Conversation title: ${conversation.title || 'Untitled'}

Transcript:
${transcript}

Return ONLY valid JSON with this exact structure:
{
  "key_points": ["point 1", "point 2"],
  "constraints": ["constraint 1"],
  "next_steps": ["step 1", "step 2", "step 3"],
  "open_questions": ["question 1", "question 2"]
}

Do not include markdown. Keep items concise.`;

      // Use existing openaiService with correct method
      const { data: aiResponse, error: aiError } = await openaiService.createChatCompletion(
        [{ role: 'user', content: prompt }],
        {
          model: 'gpt-4o',
          temperature: 0.7,
          maxTokens: 800
        }
      );

      if (aiError || !aiResponse) {
        return { data: null, error: aiError || 'AI generation failed' };
      }

      const content = aiResponse.choices[0].message.content;
      const parsed = JSON.parse(content);
      return { data: parsed, error: null };

    } catch (error) {
      console.error('Error generating doc content:', error);
      return { data: null, error };
    }
  },

  /**
   * BACKWARDS COMPATIBLE ALIAS: generatePlanContent
   * @deprecated Use generateDocContent instead
   */
  async generatePlanContent(conversation, messages) {
    return this.generateDocContent(conversation, messages);
  },

  /**
   * Convert AI response to TipTap JSON
   * @param {Object} aiResponse - AI generated content with key_points, constraints, etc.
   * @param {string} conversationId - Conversation UUID
   * @param {string} conversationTitle - Title of conversation
   * @returns {Object} TipTap JSON document
   */
  convertAIResponseToTipTapJSON(aiResponse, conversationId, conversationTitle) {
    return {
      type: 'doc',
      content: [
        {
          type: 'heading',
          attrs: { level: 1 },
          content: [{ type: 'text', text: conversationTitle || 'Chat' }]
        },
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Key Points' }]
        },
        {
          type: 'bulletList',
          content: (aiResponse.key_points || aiResponse.goals || []).map(point => ({
            type: 'listItem',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: point }] }]
          }))
        },
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Constraints' }]
        },
        {
          type: 'bulletList',
          content: (aiResponse.constraints || []).map(c => ({
            type: 'listItem',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: c }] }]
          }))
        },
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Next Steps' }]
        },
        {
          type: 'orderedList',
          content: (aiResponse.next_steps || []).map(step => ({
            type: 'listItem',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: step }] }]
          }))
        },
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Open Questions' }]
        },
        {
          type: 'bulletList',
          content: (aiResponse.open_questions || []).map(q => ({
            type: 'listItem',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: q }] }]
          }))
        },
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Source' }]
        },
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: 'Conversation ID: ' },
            { type: 'text', text: conversationId, marks: [{ type: 'code' }] }
          ]
        }
      ]
    };
  },

  /**
   * Get all plans for a user
   * @param {string} userId - User UUID
   * @returns {Promise<{data: Array, error: any}>}
   */
  async getUserPlans(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .eq('type', 'plan')
        .order('updated_at', { ascending: false});

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user plans:', error);
      return { data: null, error };
    }
  },

  /**
   * Get plans for a specific focus
   * @param {string} userId - User UUID
   * @param {string} focusId - Focus UUID
   * @returns {Promise<{data: Array, error: any}>}
   */
  async getPlansByFocus(userId, focusId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .eq('type', 'plan')
        .or(`source_focus_id.eq.${focusId},focus_ids.cs.{${focusId}}`)
        .order('updated_at', { ascending: false});

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching plans by focus:', error);
      return { data: null, error };
    }
  },

  /**
   * Get recent docs for a user
   * @param {string} userId - User UUID
   * @param {Object} options - { limit: number, excludeId: string }
   * @returns {Promise<{data: Array, error: any}>}
   */
  async getRecentDocs(userId, { limit = 10, excludeId = null } = {}) {
    try {
      let query = supabase
        .schema('public')
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(limit);
      
      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching recent docs:', error);
      return { data: null, error };
    }
  },

  /**
   * Get recent docs for a specific focus
   * @param {string} userId - User UUID
   * @param {string} focusId - Focus UUID
   * @param {number} limit - Max docs to return
   * @returns {Promise<{data: Array, error: any}>}
   */
  async getRecentDocsByFocus(userId, focusId, limit = 10) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .eq('source_focus_id', focusId)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching docs by focus:', error);
      return { data: null, error };
    }
  },

  /**
   * Create a new doc with minimal options
   * @param {string} userId - User UUID
   * @param {Object} options - { title, source_focus_id, source_conversation_id, source_item_ids, content_json }
   * @returns {Promise<{data: Object, error: any}>}
   */
  async createDoc(userId, options = {}) {
    try {
      const {
        title = 'Untitled',
        source_focus_id = null,
        source_conversation_id = null,
        source_item_ids = null,
        content_json = { type: 'doc', content: [{ type: 'paragraph' }] }
      } = options;

      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .insert({
          user_id: userId,
          title,
          type: 'doc',
          content_json,
          source_focus_id,
          source_conversation_id,
          source_item_ids,
          focus_ids: source_focus_id ? [source_focus_id] : []
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating doc:', error);
      return { data: null, error };
    }
  }
};

export default documentService;

